import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.updateDisplayedTotalPrice);
    document.addEventListener(ThemeEvents.cartUpdate, this.updateDisplayedTotalPrice);
    this.updateDisplayedTotalPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.updateDisplayedTotalPrice);
    document.removeEventListener(ThemeEvents.cartUpdate, this.updateDisplayedTotalPrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateDisplayedTotalPrice();
  };

  /**
   * Updates the displayed total price based on the selected quantity.
   * @param {Event} [event]
   */
  updateDisplayedTotalPrice = (event) => {
    if (event && 'detail' in event && event.detail?.cartLine) return;

    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    const form = this.closest('.shopify-section, dialog')?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`
    );
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return;

    const quantity = Math.max(parseInt(quantityInput.value || '1', 10) || 1, 1);
    const cartQuantity = Math.max(parseInt(quantityInput.dataset.cartQuantity || '0', 10) || 0, 0);

    const basePrice = parseInt(priceContainer.dataset.basePrice || '', 10);
    const compareAtPrice = parseInt(priceContainer.dataset.compareAtPrice || '0', 10) || 0;
    const moneyFormat = priceContainer.dataset.moneyFormat;
    const currencyCode = priceContainer.dataset.currencyCode;

    if (!basePrice || !moneyFormat || !currencyCode) return;

    const priceBreaks = this.#parsePriceBreaks(priceContainer.dataset.priceBreaks);
    const activeUnitPrice = this.#getActiveUnitPrice(basePrice, priceBreaks, cartQuantity + quantity);

    this.#updatePriceText('.price', activeUnitPrice * quantity, moneyFormat, currencyCode);

    if (compareAtPrice > activeUnitPrice) {
      this.#updatePriceText('.compare-at-price', compareAtPrice * quantity, moneyFormat, currencyCode);
    }
  };

  /**
   * @param {string | undefined} priceBreaksData
   * @returns {Array<{quantity: number, price: number}>}
   */
  #parsePriceBreaks(priceBreaksData) {
    if (!priceBreaksData) return [];

    try {
      const parsedPriceBreaks = JSON.parse(priceBreaksData);
      if (!Array.isArray(parsedPriceBreaks)) return [];

      return parsedPriceBreaks
        .map((priceBreak) => ({
          quantity: parseInt(priceBreak.quantity, 10) || 0,
          price: parseInt(priceBreak.price, 10) || 0,
        }))
        .filter((priceBreak) => priceBreak.quantity > 0 && priceBreak.price > 0)
        .sort((a, b) => b.quantity - a.quantity);
    } catch (error) {
      console.error('Failed to parse price breaks for product price:', error);
      return [];
    }
  }

  /**
   * @param {number} basePrice
   * @param {Array<{quantity: number, price: number}>} priceBreaks
   * @param {number} totalQuantity
   * @returns {number}
   */
  #getActiveUnitPrice(basePrice, priceBreaks, totalQuantity) {
    const matchingPriceBreak = priceBreaks.find((priceBreak) => totalQuantity >= priceBreak.quantity);
    return matchingPriceBreak?.price || basePrice;
  }

  /**
   * @param {string} selector
   * @param {number} amount
   * @param {string} moneyFormat
   * @param {string} currencyCode
   */
  #updatePriceText(selector, amount, moneyFormat, currencyCode) {
    for (const element of this.querySelectorAll(selector)) {
      if (!(element instanceof HTMLElement)) continue;
      element.textContent = formatMoney(amount, moneyFormat, currencyCode);
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
